// <dy-review-carousel>: arrows, keyboard, mouse drag and a "1 / 9" counter for
// a scroll-snap track. No dependencies; without JS the track still scrolls.

class DyReviewCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;
    this.items = [...this.track.children];
    this.prev = this.querySelector('[data-prev]');
    this.next = this.querySelector('[data-next]');
    this.current = this.querySelector('[data-current]');
    this.smooth = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

    this.prev?.addEventListener('click', () => this.#page(-1));
    this.next?.addEventListener('click', () => this.#page(1));

    this.track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') { event.preventDefault(); this.#step(1); }
      if (event.key === 'ArrowLeft') { event.preventDefault(); this.#step(-1); }
    });

    this.track.addEventListener('scroll', () => this.#update(), { passive: true });
    new ResizeObserver(() => this.#update()).observe(this.track);
    this.#setupDrag();
    this.#update();
  }

  #stepWidth() {
    const first = this.items[0];
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return first ? first.getBoundingClientRect().width + gap : this.track.clientWidth;
  }

  #step(direction) {
    this.track.scrollBy({ left: direction * this.#stepWidth(), behavior: this.smooth });
  }

  #page(direction) {
    const perView = Math.max(1, Math.floor(this.track.clientWidth / this.#stepWidth()));
    this.track.scrollBy({ left: direction * perView * this.#stepWidth(), behavior: this.smooth });
  }

  #update() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    if (this.prev) this.prev.disabled = scrollLeft <= 4;
    if (this.next) this.next.disabled = scrollLeft >= scrollWidth - clientWidth - 4;
    if (this.current) {
      const index = Math.round(scrollLeft / this.#stepWidth());
      this.current.textContent = String(Math.min(this.items.length, index + 1));
    }
  }

  // Click-and-drag for mouse users (touch and trackpads scroll natively).
  #setupDrag() {
    let startX = 0;
    let startScroll = 0;
    let dragging = false;
    let moved = false;

    this.track.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      dragging = true;
      moved = false;
      startX = event.clientX;
      startScroll = this.track.scrollLeft;
    });

    window.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      const dx = event.clientX - startX;
      if (Math.abs(dx) > 4) {
        moved = true;
        this.track.classList.add('is-dragging');
      }
      if (moved) this.track.scrollLeft = startScroll - dx;
    });

    window.addEventListener('pointerup', () => {
      if (!dragging) return;
      dragging = false;
      this.track.classList.remove('is-dragging');
      if (moved) {
        // Snap to the nearest card after a drag.
        const step = this.#stepWidth();
        this.track.scrollTo({ left: Math.round(this.track.scrollLeft / step) * step, behavior: this.smooth });
      }
    });

    // A drag shouldn't follow the "Shop Now" link underneath it.
    this.track.addEventListener('click', (event) => {
      if (moved) { event.preventDefault(); moved = false; }
    }, true);
  }
}

if (!customElements.get('dy-review-carousel')) customElements.define('dy-review-carousel', DyReviewCarousel);
